import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DownloadConfigFile from '../DownloadConfigFile/DownloadConfigFile';

interface PatchType {
  sid: string;
}

interface GeneratedConfigProps {
  configText: string;
  patches: PatchType[];
  placeholders: { sid: string };
  operationType?: 'patch' | 'upgrade';
  blink?: boolean;
  copied?: boolean;
  onCopy?: () => void;
  onConfigLoaded?: (data: any) => void;
  invalid?: boolean;
}

const accentColor = '#00bcd4'; // Use your app's accent/cyan color

const GeneratedConfig: React.FC<GeneratedConfigProps> = ({
  configText,
  patches,
  placeholders,
  operationType = 'patch',
  blink,
  copied = false,
  onCopy,
  onConfigLoaded,
  invalid = false,
}) => {
  const prefix = operationType === 'upgrade' ? 'upg' : 'patch';
  return (
    <>
      <Paper
        variant="outlined"
        sx={theme => ({
          p: 0,
          mb: 2,
          background: invalid ? (theme.palette.mode === 'light' ? '#fff6f6' : '#2d1a1a') : 'background.paper',
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
          border: blink
            ? '2.5px solid'
            : invalid
            ? '2.5px solid #d32f2f'
            : `2.5px solid ${theme.palette.mode === 'light' ? '#003366' : '#00bcd4'}`,
          borderColor: blink
            ? accentColor
            : invalid
            ? '#d32f2f'
            : theme.palette.mode === 'light'
            ? '#003366'
            : '#00bcd4',
          color: invalid ? '#d32f2f' : 'text.primary',
          animation: blink ? 'blink-cyan-paper 0.6s linear 0s 2' : undefined,
          '@keyframes blink-cyan-paper': {
            '0%': { background: 'background.paper', borderColor: theme.palette.mode === 'light' ? '#003366' : '#00bcd4' },
            '20%': { background: accentColor, borderColor: accentColor },
            '40%': { background: 'background.paper', borderColor: theme.palette.mode === 'light' ? '#003366' : '#00bcd4' },
            '60%': { background: accentColor, borderColor: accentColor },
            '80%': { background: 'background.paper', borderColor: theme.palette.mode === 'light' ? '#003366' : '#00bcd4' },
            '100%': { background: 'background.paper', borderColor: theme.palette.mode === 'light' ? '#003366' : '#00bcd4' },
          },
        })}
      >
        {/* Header with Title and Icon */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 3,
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            background: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)',
          }}
        >
          <DescriptionOutlinedIcon sx={{ color: theme => theme.palette.mode === 'light' ? '#003366' : '#00bcd4', fontSize: 24 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', color: theme => theme.palette.mode === 'light' ? '#003366' : '#00bcd4' }}>
            Generated Config
          </Typography>
        </Box>

        {/* Config Content */}
        <Box sx={{ position: 'relative', width: '100%', px: 3, py: 2.5 }}>
          <Box
            component="pre"
            sx={{
              fontFamily: 'monospace',
              fontSize: 13,
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
              m: 0,
              color: 'text.primary',
              userSelect: 'text',
              width: '100%',
              minHeight: 120,
              boxSizing: 'border-box',
              background: 'none',
              border: 'none',
            }}
          >
            {configText}
          </Box>
        </Box>

        {/* Buttons Section */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            px: 3,
            py: 2.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            background: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.01)' : 'rgba(255,255,255,0.01)',
          }}
        >
          <DownloadConfigFile
            configText={configText}
            patches={patches}
            placeholders={placeholders}
            prefix={prefix}
            onCopy={onCopy}
            disabled={invalid}
          />
        </Box>
      </Paper>
    </>
  );
};

export default GeneratedConfig; 